/**
 * The language chosen on THIS device by someone with no account to store it on.
 *
 * The sign-in, password-reset, invitation-accept and consent screens all render
 * before there is a session, and they are the surfaces a tenant's own users
 * meet on a custom domain. A switcher there has nowhere server-side to write to,
 * so the choice lives in `localStorage` against this origin.
 *
 * # Where it sits in the order
 *
 *   1. this device choice                        stored in localStorage
 *   2. the signed-in user's `preferred_locale`   stored on their account
 *   3. `navigator.languages`
 *   4. `en`
 *
 * The device beats the account, which is the reverse of what this said while
 * one field meant both things. It changed when the two questions were split:
 * `preferred_locale` is what Fóir WRITES to you in, set on the account page and
 * read by a server sending mail, which has no device to consult. The control in
 * the console's own footer is about the console, on this machine, now — the
 * same shape of setting as the theme toggle beside it. Someone who reaches for
 * it while signed in has said something more specific than their account did,
 * and an account setting that overrode it would make the control look broken.
 *
 * Both still beat the browser, for the reason they always did: someone who
 * picked a language from a control meant it more than their OS did.
 *
 * A signed-in user with no device choice still gets their account preference,
 * so setting the language Fóir emails you in also moves the console when a
 * catalogue for it exists. A tag with no catalogue falls through to the browser
 * rather than showing a half-translated screen, which is what makes the two
 * settings safe to leave pointing at different languages.
 *
 * # Why localStorage and not a cookie
 *
 * Nothing on the server reads this. The apps are client-side SPAs with no SSR,
 * so there is no render that would benefit from the value arriving on the
 * request, and a cookie would be sent on every API call for nothing.
 */

const KEY = 'foir.locale';

/** Read the stored choice, or null. Safe where storage is blocked or absent. */
export function readDeviceLocale(): string | null {
  try {
    const value = globalThis.localStorage?.getItem(KEY);
    return value && value.trim() ? value : null;
  } catch {
    // Private mode, a blocked third-party context, or no DOM at all. A missing
    // preference is a real answer here, so this is not worth surfacing.
    return null;
  }
}

/** Store a choice, or clear it with `null` to go back to following the browser. */
export function writeDeviceLocale(locale: string | null): void {
  try {
    if (locale) globalThis.localStorage?.setItem(KEY, locale);
    else globalThis.localStorage?.removeItem(KEY);
  } catch {
    // Losing the write means the choice does not survive a reload. The UI has
    // already changed language by the time this runs, so failing silently
    // degrades better than refusing the interaction.
  }
}
